import React from 'react';
import SafeImage from '../../SafeImage';
import { API_BASE_URL } from '../../../api';

// All the fields shared by Add and Edit Hero Slide — Edit additionally shows
// a "Current Image" preview and makes the upload optional.
const HeroSlideFormFields = ({ form, setForm, currentImage, currentImageTitle }) => (
  <>
    {/* Basic Information */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter slide title"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
          <input
            type="text"
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter slide subtitle"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter slide description"
          />
        </div>
      </div>
    </div>

    {/* Badge & Styling */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Badge & Styling</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
          <input
            type="text"
            value={form.badge}
            onChange={(e) => setForm({ ...form, badge: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., New Collection, Best Sellers"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Badge Color</label>
          <input
            type="color"
            value={form.accent}
            onChange={(e) => setForm({ ...form, accent: e.target.value })}
            className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
          <input
            type="color"
            value={form.textColor}
            onChange={(e) => setForm({ ...form, textColor: e.target.value })}
            className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
          />
        </div>
      </div>
    </div>

    {/* Gradient Settings */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Gradient Settings</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gradient</label>
          <select
            value={form.gradient}
            onChange={(e) => setForm({ ...form, gradient: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="from-[#d44100] to-[#0f4c5c]">Orange to Blue</option>
            <option value="from-[#0f4c5c] to-[#d44100]">Blue to Orange</option>
            <option value="from-[#be1349] to-[#f8aeae]">Red to Pink</option>
            <option value="from-[#1e3a8a] to-[#3b82f6]">Dark Blue to Blue</option>
            <option value="from-[#7c2d12] to-[#f97316]">Brown to Orange</option>
            <option value="from-[#374151] to-[#6b7280]">Gray Gradient</option>
          </select>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="gradientEnabled"
            checked={form.gradientEnabled}
            onChange={(e) => setForm({ ...form, gradientEnabled: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="gradientEnabled" className="ml-2 block text-sm text-gray-700">Enable Gradient Overlay</label>
        </div>
      </div>
    </div>

    {/* Buttons */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Action Buttons</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
          <input
            type="text"
            value={form.buttonText}
            onChange={(e) => setForm({ ...form, buttonText: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Shop Now, Explore"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Action</label>
          <select
            value={form.buttonAction}
            onChange={(e) => setForm({ ...form, buttonAction: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Action</option>
            <option value="category:cat-shoulder">Navigate to Shoulder Bags</option>
            <option value="category:cat-tote">Navigate to Tote Bags</option>
            <option value="category:cat-crossbody">Navigate to Crossbody Bags</option>
            <option value="scroll:products">Scroll to Products</option>
            <option value="scroll:about">Scroll to About</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
          <input
            type="text"
            value={form.secondaryButtonText}
            onChange={(e) => setForm({ ...form, secondaryButtonText: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Learn More"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Action</label>
          <select
            value={form.secondaryButtonAction}
            onChange={(e) => setForm({ ...form, secondaryButtonAction: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Action</option>
            <option value="scroll:about">Scroll to About</option>
            <option value="scroll:products">Scroll to Products</option>
            <option value="category:cat-shoulder">Navigate to Shoulder Bags</option>
            <option value="category:cat-tote">Navigate to Tote Bags</option>
            <option value="category:cat-crossbody">Navigate to Crossbody Bags</option>
          </select>
        </div>
      </div>
    </div>

    {/* Current Image (edit mode only) */}
    {currentImage && (
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Current Image</h4>
        <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-200">
          <SafeImage
            src={`${API_BASE_URL}${currentImage}`}
            alt={currentImageTitle}
            className="w-full h-full object-cover"
            fallback={<div className="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500 text-xs">No Image</div>}
          />
        </div>
      </div>
    )}

    {/* Image Upload */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">{currentImage ? 'Update Image' : 'Slide Image'}</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{currentImage ? 'Upload New Image' : 'Upload Image *'}</label>
        <input
          type="file"
          name="image"
          accept="image/*"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required={!currentImage}
        />
        <p className="text-xs text-gray-500 mt-1">
          {currentImage ? 'Leave empty to keep current image' : 'Recommended: 1920x1080px or similar aspect ratio'}
        </p>
      </div>
    </div>

    {/* Animation & Style */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Animation & Style</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Animation Type</label>
          <select
            value={form.animationType}
            onChange={(e) => setForm({ ...form, animationType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="fade">Fade</option>
            <option value="slide">Slide</option>
            <option value="zoom">Zoom</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Button Style</label>
          <select
            value={form.buttonStyle}
            onChange={(e) => setForm({ ...form, buttonStyle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="outline">Outline</option>
          </select>
        </div>
      </div>
    </div>

    {/* Status */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Status</h4>
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">Active (visible on homepage)</label>
      </div>
    </div>
  </>
);

export default HeroSlideFormFields;
